import { WidthPreset } from './contract';

// Width sizing model (phase 2). Width is a fraction of the page content width
// (layout.widthFraction, 0..1) — the single source of truth honored identically
// in the editor preview and by the runner in all three formats. Presets snap to
// clean fractions; free-drag (Shift) produces 'custom'.

export interface SizingSpec {
  // Selectable presets (excludes 'custom', which only ever results from a drag).
  presets: WidthPreset[];
  // Clamp floor for edge-drag, as a fraction of content width.
  minFraction: number;
  // Enables the edge-drag handle.
  resizable: boolean;
}

export const PRESET_FRACTION: Partial<Record<WidthPreset, number>> = {
  full: 1,
  twoThirds: 2 / 3,
  half: 0.5,
  third: 1 / 3,
  quarter: 0.25,
};

export const PRESET_LABEL: Partial<Record<WidthPreset, string>> = {
  full: 'Full width',
  twoThirds: 'Two thirds',
  half: 'Half',
  third: 'One third',
  quarter: 'Quarter',
};

// Components that don't opt into sizing stay full-width and non-resizable.
export const DEFAULT_SIZING: SizingSpec = { presets: ['full'], minFraction: 1, resizable: false };

// Nearest allowed preset whose fraction is within `threshold` of `fraction`, else
// null (→ 'custom'). threshold is in fraction units (0.03 ≈ 3% of the width).
export function snapToPreset(fraction: number, presets: WidthPreset[], threshold = 0.03): WidthPreset | null {
  let best: WidthPreset | null = null;
  let bestDelta = threshold;
  for (const preset of presets) {
    const f = PRESET_FRACTION[preset];
    if (f == null) continue;
    const delta = Math.abs(f - fraction);
    if (delta <= bestDelta) {
      bestDelta = delta;
      best = preset;
    }
  }
  return best;
}
